import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Col, Form, Row } from 'antd';

import { DEFAULT_DATE_FORMAT } from '@/contants/common';
import { formatDate } from '@/utils/date';
import { convertDateUTCToUserLocal } from '@/utilz/dates';
import { routeFileSelector } from '@/utilz/i18n';
import FormSelectBox from '@/views/components/Inputs/FormSelectBox';
import FormTextArea from '@/views/components/Inputs/FormTextArea';
import HiddenFormInput from '@/views/components/Inputs/HiddenFormInput';
import FormLabel from '@/views/components/Labels/FormLabel';
import SecondaryLabel from '@/views/components/Labels/SecondaryLabel';
import Paper from '@/views/components/Paper/Paper';
import { useFocusableRef } from '@/views/screens/Claims/common/hooks/useFocusableRef';
import TMSFiledCard from '@/views/screens/common/components/TMSFiledCard';
import SaveCancelButtons from '@/views/screens/Shipment/components/SaveCancelButtons';

import { PRE_CLAIMS_TYPES_OPTIONS } from '../../../../constants';

const PreClaimsNotesCard = ({
  editMode = false,
  isLastItem = false,
  shadowVisible = true,
  index = 0,
  noteDetails,
  setEditMode,
  handleCancel,
  handleSubmit,
  handleDelete,
  onFieldsChange,
}) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const [form] = Form.useForm();
  const commentText = Form.useWatch('commentText', form);

  useEffect(() => {
    if (noteDetails) {
      const formValues = {
        ...noteDetails,
        commentText: noteDetails.commentText ?? null,
        commentType: noteDetails.commentType ?? null,
      };
      form.resetFields();
      form.setFieldsValue(formValues);
    }
  }, [noteDetails, form]);

  const cardRef = useFocusableRef('#commentType', { isEnabled: noteDetails?.isNew && !noteDetails?.isInitial });

  return (
    <div ref={cardRef}>
      <Paper className={`border-muted ${!isLastItem ? 'mb-16' : ''} ${shadowVisible ? '' : 'shadow-none'}`}>
        <Form onFinish={(data) => handleSubmit(data, index)} form={form} onFieldsChange={onFieldsChange}>
          <div className="border-bottom-muted pt-8 pb-10 px-16">
            <Row justify="space-between align-items-center">
              <Col sm={6}>
                <Row align="middle" gutter={15}>
                  <Col className="flex-1">
                    <FormSelectBox
                      options={PRE_CLAIMS_TYPES_OPTIONS}
                      label={t('translation:claims.commentType')}
                      placeholder={t('translation:claims.selectCommentType')}
                      dataTestId="pre-claims-comment-select-type"
                      margin="mb-0"
                      name="commentType"
                      editMode={editMode}
                      rules={[
                        {
                          message: 'Please select a Comment Type',
                          required: true,
                        },
                      ]}
                      shouldUseNativeSelect
                    />
                  </Col>
                </Row>
              </Col>
              <Col>
                <SaveCancelButtons
                  editMode={editMode}
                  lightMode={false}
                  setEditMode={(val) => setEditMode(index, val)}
                  handleRemove={() => handleDelete(index, noteDetails.isNew)}
                  isNew={noteDetails.isNew}
                  idSuffix={`product-details-${index + 1}`}
                  isModified
                  onCancel={() => handleCancel(index, noteDetails.isNew)}
                />
              </Col>
            </Row>
          </div>
          <div className="px-15 pt-10">
            <Row gutter={15}>
              <Col sm={8}>
                {editMode ? (
                  <FormTextArea
                    label={t('translation:claims.comments')}
                    dataTestId="pre-claims-comment-text"
                    placeholder={t('translation:claims.enterComments')}
                    editMode={editMode}
                    {...(editMode ? { rows: 3 } : {})}
                    name="commentText"
                  />
                ) : (
                  <div className="mb-10">
                    <FormLabel>{t('translation:claims.comments')}</FormLabel>
                    <br />
                    <SecondaryLabel className="white-space-pre-wrap f-16 fw-600">{commentText}</SecondaryLabel>
                  </div>
                )}
                <HiddenFormInput name="commentText" />
              </Col>
              <Col sm={4}>
                <TMSFiledCard containerClass="mt-1" title={t('translation:default.fields.createdOn.title')}>
                  {noteDetails?.createdOn
                    ? formatDate(
                        convertDateUTCToUserLocal(noteDetails?.createdOn)?.format('YYYY-MM-DDTHH:mm:ss.SSS') ||
                          noteDetails?.createdOn,
                        DEFAULT_DATE_FORMAT,
                      )
                    : ''}
                </TMSFiledCard>
              </Col>
              <Col sm={4}>
                <TMSFiledCard containerClass="mt-2" title={t('translation:claims.createdBy')}>
                  {noteDetails.createdByUser}
                </TMSFiledCard>
              </Col>
              <Col sm={4}>
                <TMSFiledCard containerClass="mt-1" title={t('translation:default.fields.modifiedOn')}>
                  {noteDetails?.modifiedOn
                    ? formatDate(
                        convertDateUTCToUserLocal(noteDetails?.modifiedOn)?.format('YYYY-MM-DDTHH:mm:ss.SSS') ||
                          noteDetails?.modifiedOn,
                        DEFAULT_DATE_FORMAT,
                      )
                    : ''}
                </TMSFiledCard>
              </Col>
              <Col sm={4}>
                <TMSFiledCard containerClass="mt-2" title={t('translation:default.fields.modifiedBy')}>
                  {noteDetails.modifiedByUser}
                </TMSFiledCard>
              </Col>
            </Row>
          </div>
          <HiddenFormInput name="sequenceNo" />
          <HiddenFormInput name="oldCommentType" />
          <HiddenFormInput name="isNew" />
          <HiddenFormInput name="editMode" />
          <HiddenFormInput name="claimKey" />
          <HiddenFormInput name="dirtyFlag" />
          <HiddenFormInput name="createdBy" />
          <HiddenFormInput name="modifiedBy" />
        </Form>
      </Paper>
    </div>
  );
};

export default PreClaimsNotesCard;
